// ============================================================
// انتخابگر موقعیت مکانی روی نقشه
//
// کاربر با کلیک روی نقشه، کشیدن پین، جست‌وجوی نام مکان، یا موقعیت
// فعلی دستگاه مختصات را انتخاب می‌کند. مقدار از طریق onChange
// برگردانده می‌شود.
//
// نسخه‌ی قبلی روی یک تصویر استاتیک نشان کار می‌کرد و درگ/زوم/تبدیل
// نقطه‌ی کلیک به مختصات را با دست حساب می‌کرد (و کلید API هم داخل
// باندل بود). حالا Leaflet این کارها را می‌کند و جست‌وجو با
// Nominatim است — هر دو رایگان و بی‌کلید.
// ============================================================

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MapContainer, Marker, useMap, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { Loader2, LocateFixed, MapPin, Minus, Plus, Search, Trash2, X, ImageOff } from "lucide-react";

import MapAutoSize from "./map/MapAutoSize";
import TileLayerWithFallback from "./map/TileLayerWithFallback";
import { PlaceResult, searchPlaces } from "./map/geocode";
import { DEFAULT_CENTER, MAX_ZOOM, MIN_ZOOM, pulsePin, round6, useIsDarkMode } from "./map/tiles";

const DEFAULT_PICK_ZOOM = 16;
const OVERVIEW_ZOOM = 11;

interface LocationPickerMapProps {
    lat: number | null;
    lng: number | null;
    onChange: (lat: number, lng: number) => void;
    onClear?: () => void;
    height?: number;
}

/** کلیک روی نقشه = انتخاب همان نقطه. */
const ClickToPick: React.FC<{ onPick: (lat: number, lng: number) => void }> = ({ onPick }) => {
    useMapEvents({
        click: (event) => onPick(round6(event.latlng.lat), round6(event.latlng.lng)),
    });
    return null;
};

/**
 * حرکت دادن نقشه از بیرون (جست‌وجو، موقعیت من، دکمه‌های زوم).
 *
 * `target` فقط وقتی عوض می‌شود که *ما* بخواهیم نقشه جابه‌جا شود؛ با
 * انتخاب نقطه‌ای که کاربر خودش کلیک کرده، نقشه نمی‌پرد.
 */
const MapController: React.FC<{
    target: { lat: number; lng: number; zoom: number } | null;
    zoom: number;
}> = ({ target, zoom }) => {
    const map = useMap();

    useEffect(() => {
        if (target) map.setView([target.lat, target.lng], target.zoom);
    }, [map, target]);

    useEffect(() => {
        if (map.getZoom() !== zoom) map.setZoom(zoom);
    }, [map, zoom]);

    return null;
};

const LocationPickerMap: React.FC<LocationPickerMapProps> = ({
    lat,
    lng,
    onChange,
    onClear,
    height = 320,
}) => {
    const hasValue = lat != null && lng != null;
    const dark = useIsDarkMode();

    const [zoom, setZoom] = useState(hasValue ? DEFAULT_PICK_ZOOM : OVERVIEW_ZOOM);
    const [target, setTarget] = useState<{ lat: number; lng: number; zoom: number } | null>(null);
    const [failed, setFailed] = useState(false);
    const [mapKey, setMapKey] = useState(0);

    // ===== جست‌وجو =====
    const [term, setTerm] = useState("");
    const [results, setResults] = useState<PlaceResult[] | null>(null);
    const [searching, setSearching] = useState(false);
    const [notice, setNotice] = useState("");
    const [locating, setLocating] = useState(false);

    const initialCenter = useMemo<[number, number]>(
        () => (hasValue ? [lat as number, lng as number] : DEFAULT_CENTER),
        // فقط مرکز اولیه است؛ با هر تغییر مقدار نباید نقشه از نو ساخته شود
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const pick = useCallback(
        (nextLat: number, nextLng: number) => {
            setNotice("");
            onChange(nextLat, nextLng);
        },
        [onChange]
    );

    /** مثل کلیک، ولی نقشه را هم روی نقطه می‌برد. */
    const pickAndGo = useCallback(
        (nextLat: number, nextLng: number, nextZoom: number = DEFAULT_PICK_ZOOM) => {
            pick(nextLat, nextLng);
            setZoom(nextZoom);
            setTarget({ lat: nextLat, lng: nextLng, zoom: nextZoom });
        },
        [pick]
    );

    const runSearch = async () => {
        const query = term.trim();
        if (!query) return;

        setSearching(true);
        setNotice("");
        setResults(null);
        try {
            const items = await searchPlaces(query);
            setResults(items);
            if (!items.length) setNotice("نتیجه‌ای یافت نشد.");
        } catch {
            setNotice("جست‌وجو در دسترس نیست. می‌توانید موقعیت را مستقیماً روی نقشه انتخاب کنید.");
        } finally {
            setSearching(false);
        }
    };

    const selectResult = (place: PlaceResult) => {
        pickAndGo(round6(place.lat), round6(place.lng));
        setResults(null);
        setTerm(place.title);
    };

    const useMyLocation = () => {
        if (!navigator.geolocation) {
            setNotice("مرورگر شما موقعیت‌یابی را پشتیبانی نمی‌کند.");
            return;
        }
        setLocating(true);
        navigator.geolocation.getCurrentPosition(
            (position) => {
                pickAndGo(round6(position.coords.latitude), round6(position.coords.longitude));
                setLocating(false);
            },
            () => {
                setNotice("دسترسی به موقعیت مکانی ممکن نشد.");
                setLocating(false);
            },
            { enableHighAccuracy: true, timeout: 10000 }
        );
    };

    useEffect(() => {
        if (!hasValue) setResults(null);
    }, [hasValue]);

    // کشیدن پین = جابه‌جا کردن موقعیت انتخاب‌شده
    const markerHandlers = useMemo(
        () => ({
            dragend: (event: L.LeafletEvent) => {
                const position = (event.target as L.Marker).getLatLng();
                pick(round6(position.lat), round6(position.lng));
            },
        }),
        [pick]
    );

    const btn =
        "flex items-center justify-center transition bg-white/95 dark:bg-zink-700/95 text-slate-700 dark:text-zink-100 size-8 rounded-lg shadow-md hover:bg-white dark:hover:bg-zink-600 disabled:opacity-40 disabled:cursor-not-allowed";

    return (
        <div className="space-y-2">
            {/* ===== نوار جست‌وجو ===== */}
            <div className="relative">
                <div className="flex gap-2">
                    <div className="relative flex-1">
                        <Search className="absolute -translate-y-1/2 right-3 top-1/2 size-4 text-slate-400" />
                        <input
                            type="text"
                            className="w-full text-sm form-input pr-9"
                            placeholder="جست‌وجوی نام مکان یا آدرس..."
                            value={term}
                            onChange={(event) => setTerm(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                    event.preventDefault();
                                    runSearch();
                                }
                            }}
                        />
                        {term && (
                            <button
                                type="button"
                                onClick={() => {
                                    setTerm("");
                                    setResults(null);
                                    setNotice("");
                                }}
                                className="absolute -translate-y-1/2 left-3 top-1/2 text-slate-400 hover:text-slate-600"
                            >
                                <X className="size-4" />
                            </button>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={runSearch}
                        disabled={searching || !term.trim()}
                        className="px-4 py-2 text-sm text-white transition-colors rounded-lg bg-custom-500 hover:bg-custom-600 disabled:opacity-50"
                    >
                        {searching ? <Loader2 className="size-4 animate-spin" /> : "جست‌وجو"}
                    </button>
                    <button
                        type="button"
                        onClick={useMyLocation}
                        disabled={locating}
                        title="استفاده از موقعیت فعلی من"
                        className="flex items-center justify-center px-3 transition-colors border rounded-lg border-slate-300 dark:border-zink-500 hover:bg-slate-100 dark:hover:bg-zink-600 disabled:opacity-50"
                    >
                        {locating ? (
                            <Loader2 className="size-4 animate-spin" />
                        ) : (
                            <LocateFixed className="size-4" />
                        )}
                    </button>
                </div>

                {results && results.length > 0 && (
                    <div className="absolute z-20 w-full mt-1 overflow-y-auto bg-white border shadow-lg dark:bg-zink-700 rounded-xl border-slate-200 dark:border-zink-500 max-h-56">
                        {results.map((place, index) => (
                            <button
                                type="button"
                                key={index}
                                onClick={() => selectResult(place)}
                                className="flex items-start w-full gap-2 px-3 py-2 text-right transition-colors border-b last:border-none border-slate-100 dark:border-zink-600 hover:bg-slate-50 dark:hover:bg-zink-600"
                            >
                                <MapPin className="size-4 mt-0.5 text-custom-500 shrink-0" />
                                <span className="flex-1">
                                    <span className="block text-sm text-slate-800 dark:text-zink-50">
                                        {place.title}
                                    </span>
                                    {place.address && (
                                        <span className="block text-xs text-slate-400">{place.address}</span>
                                    )}
                                </span>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {notice && <p className="text-xs text-amber-600">{notice}</p>}

            {/* ===== نقشه ===== */}
            <div
                className="relative overflow-hidden border rounded-xl bg-slate-100 dark:bg-zink-600 border-slate-200 dark:border-zink-500"
                style={{ height }}
            >
                {failed ? (
                    <div className="flex flex-col items-center justify-center h-full gap-2 text-slate-400">
                        <ImageOff size={38} className="opacity-50" />
                        <p className="text-sm">نمایش نقشه ممکن نشد.</p>
                        <button
                            type="button"
                            onClick={() => {
                                setFailed(false);
                                setMapKey((k) => k + 1);
                            }}
                            className="px-3 py-1.5 text-xs border rounded-lg border-slate-300 hover:bg-slate-100 dark:hover:bg-zink-500"
                        >
                            تلاش مجدد
                        </button>
                    </div>
                ) : (
                    <MapContainer
                        key={mapKey}
                        center={initialCenter}
                        zoom={hasValue ? DEFAULT_PICK_ZOOM : OVERVIEW_ZOOM}
                        minZoom={MIN_ZOOM}
                        maxZoom={MAX_ZOOM}
                        zoomControl={false}
                        scrollWheelZoom
                        // ردگیری resize به MapAutoSize سپرده شده — توضیحش آنجاست
                        trackResize={false}
                        style={{ height: "100%", width: "100%", zIndex: 1, cursor: "crosshair" }}
                    >
                        <MapAutoSize />
                        <TileLayerWithFallback dark={dark} onExhausted={() => setFailed(true)} />
                        <ClickToPick onPick={pick} />
                        {hasValue && (
                            <Marker
                                position={[lat as number, lng as number]}
                                icon={pulsePin()}
                                draggable
                                eventHandlers={markerHandlers}
                            />
                        )}
                        <MapController target={target} zoom={zoom} />
                    </MapContainer>
                )}

                {!failed && (
                    <React.Fragment>
                        <div className="absolute z-[2] flex flex-col gap-1.5 top-3 right-3">
                            <button
                                type="button"
                                onClick={() => setZoom((z) => Math.min(MAX_ZOOM, z + 1))}
                                disabled={zoom >= MAX_ZOOM}
                                className={btn}
                                title="بزرگ‌نمایی"
                            >
                                <Plus size={16} />
                            </button>
                            <button
                                type="button"
                                onClick={() => setZoom((z) => Math.max(MIN_ZOOM, z - 1))}
                                disabled={zoom <= MIN_ZOOM}
                                className={btn}
                                title="کوچک‌نمایی"
                            >
                                <Minus size={16} />
                            </button>
                        </div>

                        <div className="absolute z-[2] px-2.5 py-1 text-[11px] rounded-lg shadow bottom-3 right-3 bg-white/90 dark:bg-zink-700/90 text-slate-600 dark:text-zink-100">
                            برای انتخاب روی نقشه کلیک کنید — پین را هم می‌توانید بکشید
                        </div>
                    </React.Fragment>
                )}
            </div>

            {/* ===== مختصات انتخاب‌شده ===== */}
            <div className="flex flex-wrap items-center gap-2">
                {hasValue ? (
                    <React.Fragment>
                        <span
                            className="px-2.5 py-1 font-mono text-xs border rounded-lg bg-slate-50 dark:bg-zink-600 text-slate-600 dark:text-zink-100 border-slate-200 dark:border-zink-500"
                            dir="ltr"
                        >
                            {Number(lat).toFixed(6)}, {Number(lng).toFixed(6)}
                        </span>
                        {onClear && (
                            <button
                                type="button"
                                onClick={onClear}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs text-red-600 transition-colors rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10"
                            >
                                <Trash2 className="size-3.5" />
                                حذف موقعیت
                            </button>
                        )}
                    </React.Fragment>
                ) : (
                    <span className="text-xs text-slate-400">
                        موقعیتی انتخاب نشده است. روی نقشه کلیک کنید یا مکان را جست‌وجو کنید.
                    </span>
                )}
            </div>
        </div>
    );
};

export default LocationPickerMap;
